import { CircularProgress, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { Route, Routes, useLocation, useNavigate, useRoutes } from 'react-router-dom';
import store, { dispatch, useSelector } from 'src/store/Store';
import { lazy, useEffect, useState } from 'react';
import { setIsLoadings, setOpen_search_menu, setPersist, setToastMessage } from './store/my_pages/app';
import { setUserJobScheduleIdFromResData } from './functions/general';
import { setDarkMode } from './store/customizer/CustomizerSlice.js';
import Loadable from './layouts/full/shared/loadable/Loadable.js';
import { setAuthAccessToken } from './store/my_pages/auth.js';
import ToastMessage from './components/shared/ToastMessage';
import ScrollToTop from './components/shared/ScrollToTop';
import AuthGuard from './components/shared/AuthGuard';
import { getApiUrl, apiConfig, getReportEndpoints, getInitialEndpoints, getScanEndpoints, getChartEndpoints, getSummaryEndpoints } from './config/apiConfig';
import axiosIn from './middlewares/axiosInterceptor';
import { ThemeSettings } from './theme/Theme';
import { AppState } from './store/Store';
import Router, { publicRoutes } from './routes/Router';
// import HistoryOfTheScan from './components/shared/ScanHistoryPicker.tsx';
import SecuseekGenerateApiKeyDialog from './my_pages/profile_settings/pages/SecuseekGenerateApiKeyDialog.tsx';
import { usePageAvailability } from './hooks/usePageAvailability';

const Maintenance = Loadable(lazy(() => import('./my_pages/authentication/Maintenance')));

const IS_UNDER_MAINTANENCE = false; // TODO: Can be dynamicly fetched.

function App() {
  const routing = useRoutes(Router);
  const theme = ThemeSettings();

  const customizer = useSelector((state: AppState) => state.customizer);
  const { authToken } = useSelector((state: AppState) => state.authReducer);

  const { persist } = useSelector((state: AppState) => state.appGeneralReducer);
  const [showServerError, setShowServerError] = useState(false);

  const navigate = useNavigate();
  const pathname = useLocation();

  // Use custom hook for page availability logic
  const { getRedirectPathForDomainChange } = usePageAvailability();

  const _publicRoutes = [
    //
    // { path: '/', element: <Login /> },
    ...publicRoutes,
  ];

  useEffect(() => {
    let notificationsTimeout: NodeJS.Timeout | null = null;

    if (authToken) {
      // Always fetch schedule_scan data when authToken is available and data is missing
      if (persist?.schedule_scan === undefined) {
        fetchSingleApi('schedule_scan');
      }
      if (persist?.notifications === undefined) {
        notificationsTimeout = setTimeout(() => {
          fetchSingleApi('notifications');
        }, 4000);
      }
    }

    // Cleanup function to clear timeout when authToken changes or component unmounts (e.g. logout)
    return () => {
      if (notificationsTimeout) {
        clearTimeout(notificationsTimeout);
      }
    };
  }, [authToken, persist?.schedule_scan]);

  useEffect(() => {
    const theme_LS = window?.localStorage.getItem('theme');
    if (theme_LS && customizer.activeMode !== theme_LS) {
      dispatch(setDarkMode(theme_LS));
    }
    // ---
    const userLS: any = window.localStorage.getItem('user');
    if (userLS) {
      dispatch(setPersist({ key: 'users_me_profile', value: JSON.parse(userLS) }));
    }
    // ---
    const authTokenLS: any = window.localStorage.getItem('access');
    if (authTokenLS) {
      dispatch(setAuthAccessToken(authTokenLS));
    } else {
      if (!authToken && !_publicRoutes.map((page: any) => page.path).includes(pathname.pathname)) {
        navigate('auth/login');
        dispatch(setToastMessage({ msg: 'You are not logged in. Please login to continue', color: 'warning' }));
      }
    }
    // ---
    const timer = setTimeout(() => {
      if (!persist?.users_me_profile) {
        setShowServerError(true);
      }
    }, 15000);
    return () => clearTimeout(timer);
  }, []);

  // Display Maintenance page if maintenance mode is enabled
  if (IS_UNDER_MAINTANENCE) {
    navigate('/');
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Maintenance />
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }

  const handleRedirectionAfterActiveDomainChange = () => {
    const activeDomain = persist?.active_domain_data;
    if (activeDomain) {
      const currentPath = pathname.pathname;
      const redirectPath = getRedirectPathForDomainChange(currentPath, activeDomain);

      if (redirectPath) {
        navigate(redirectPath);
      }
    }
  };

  useEffect(() => {
    handleRedirectionAfterActiveDomainChange();
  }, [persist?.active_domain_data]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {persist?.users_me_profile?.email ? (
          // routing with AuthGuard for protected routes
          <AuthGuard>
            <ScrollToTop>{routing}</ScrollToTop>
          </AuthGuard>
        ) : !authToken ? (
          <Routes>
            {_publicRoutes.map((r: any) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
          </Routes>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '2rem', height: '100vh' }}>
            {!showServerError && <CircularProgress color="secondary" size={30} sx={{ mt: 3 }} />}
            {showServerError ? (
              <>
                <Typography>The server cannot be reached.</Typography>
                <Typography>Refresh, or login again. If it does not help, please try again later...</Typography>
              </>
            ) : (
              <Typography>Authenticating...</Typography>
            )}
          </div>
        )}
        <ToastMessage />
        {/* <HistoryOfTheScan_old /> */}
        <SecuseekGenerateApiKeyDialog />
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;

export const initial_fetch_all = async (key = null) => {
  const state: any = store.getState();
  const authReducer = state.authReducer;
  if (!authReducer?.authToken) return;

  dispatch(setPersist({ key: 'is_initial_fetches_loading', value: true }));

  try {
    // Get initial endpoints
    const initialEndpoints = getInitialEndpoints();
    const initialApis = Object.entries(initialEndpoints).map(([key, endpoint]) => ({
      key,
      request: axiosIn.get(getApiUrl(endpoint)),
    }));

    // Process initial API requests
    const initialResponses = await Promise.allSettled(initialApis.map((api) => api.request));

    initialResponses.forEach((res: any, index) => {
      const { key } = initialApis[index];
      if (res.status === 'fulfilled') {
        const data = res?.value?.data ?? null;
        dispatch(setPersist({ key, value: data }));
      } else {
        console.error(`Initial request failed: ${key}`, res.reason);
        dispatch(setPersist({ key, value: null }));
      }
    });

    // Get the userJobScheduleId from the schedule_scan response
    const scheduleScanResponse: any = initialResponses.find((res, index) => initialApis[index].key === 'schedule_scan' && res.status === 'fulfilled');
    const userJobScheduleId = scheduleScanResponse ? setUserJobScheduleIdFromResData(scheduleScanResponse?.value?.data) : null;

    if (!userJobScheduleId) return;

    // Get report endpoints and create API requests
    const reportEndpoints = getReportEndpoints();
    const reportApis = Object.entries(reportEndpoints).map(([key, endpoint]) => ({
      key,
      request: axiosIn.get(getApiUrl(endpoint, { userJobScheduleId })),
    }));

    // Add scan status endpoint
    reportApis.push({
      key: 'activeDomainStatus',
      request: axiosIn.get(getApiUrl(apiConfig.scan_status, { id: userJobScheduleId })),
    });

    // Process report API requests
    const reportResponses = await Promise.allSettled(reportApis.map((api) => api.request));

    reportResponses.forEach((response, index) => {
      const { key } = reportApis[index];
      if (response.status === 'fulfilled') {
        const data = response?.value?.data ?? null;
        dispatch(setPersist({ key, value: data }));
      } else {
        console.error(`Report request failed: ${key}`, response.reason);
        dispatch(setPersist({ key, value: null }));
      }
    });
  } catch (error: any) {
    console.error('Unexpected error during initial fetches:', error);
    dispatch(setToastMessage({ msg: error.message || 'Unexpected error', color: 'error' }));
  } finally {
    dispatch(setPersist({ key: 'is_initial_fetches_loading', value: false }));
  }
};

export const fetchSingleApi = async (key: string, skipSettingIsLoading?: boolean) => {
  const state: any = store.getState();
  const authToken = state.authReducer?.authToken;
  if (!authToken) return;

  const { selectedHistory } = state.appGeneralReducer;
  const isHistorySelected = selectedHistory?.date;

  dispatch(setOpen_search_menu(false));

  // Combine all endpoint groups
  const apiList = {
    ...getInitialEndpoints(),
    ...getReportEndpoints(),
    ...getSummaryEndpoints(),
    ...getChartEndpoints(),
    ...getScanEndpoints(),
    issues_findings: apiConfig.issues_findings,
  };

  if (!apiList[key] && !isHistorySelected) {
    dispatch(setIsLoadings({ key: key, value: false }));
    return;
  }

  if (key === 'schedule_scan') {
    // Only prevent duplicate calls if we're not explicitly refreshing after an add/update operation
    if (state.appGeneralReducer?.isLoadings?.schedule_scan && !skipSettingIsLoading) {
      return;
    }

    if (!skipSettingIsLoading) dispatch(setIsLoadings({ key: key, value: true }));
    // Artificial delay to simulate a slow response
    // await new Promise((resolve) => setTimeout(resolve, 2500));
    try {
      const apiUrl = getApiUrl(apiList['schedule_scan']);
      const res: any = await axiosIn.get(apiUrl);
      dispatch(setIsLoadings({ key: key, value: false }));

      // Set schedule_scan first, then set userJobScheduleId
      dispatch(setPersist({ key: 'schedule_scan', value: res?.data || [] }));

      if (res?.data?.length) {
        dispatch(setPersist({ key: 'userJobScheduleId', value: setUserJobScheduleIdFromResData(res?.data) }));
      } else {
        dispatch(setPersist({ key: 'userJobScheduleId', value: '' }));
      }
      return res;
    } catch (error: any) {
      console.error('fetchSingleApi: schedule_scan API error:', error);
      dispatch(setIsLoadings({ key: key, value: false }));
      return error;
    }
  }

  if (key === 'scan_status') {
    dispatch(setIsLoadings({ key: key, value: true }));
    try {
      const apiUrl = getApiUrl(apiList['scan_status']);
      const res: any = await axiosIn.get(apiUrl);
      dispatch(setIsLoadings({ key: key, value: false }));
      if (res?.data?.length) {
        dispatch(setPersist({ key: 'userJobScheduleId', id: setUserJobScheduleIdFromResData(res?.data) }));
      }
    } catch (error: any) {
      console.error('error', error);
      dispatch(setIsLoadings({ key: key, value: false }));
      return;
    }
  }

  // scan_type_packages doesn't require userJobScheduleId - to escape noProperId below.
  if (key === 'scan_type_packages') {
    dispatch(setIsLoadings({ key: key, value: true }));
    try {
      const apiUrl = getApiUrl(apiList['scan_type_packages']);
      const res: any = await axiosIn.get(apiUrl);
      dispatch(setIsLoadings({ key: key, value: false }));
      dispatch(setPersist({ key: 'scan_type_packages', value: res?.data || [] }));
      return res;
    } catch (error: any) {
      console.error('fetchSingleApi: scan_type_packages API error:', error);
      dispatch(setIsLoadings({ key: key, value: false }));
      return error;
    }
  }

  const userJobScheduleId = state.appGeneralReducer.persist.userJobScheduleId;
  const websiteVulnerabilityId = state.appGeneralReducer.selectedHistory?.websiteVulnerabilityId;
  // const apiUrl = getApiUrl(apiList[key], { userJobScheduleId });
  const noProperId = isHistorySelected ? !websiteVulnerabilityId || websiteVulnerabilityId === 'undefined' : !userJobScheduleId || userJobScheduleId === 'undefined';
  if (noProperId) return;
  const apiUrl = isHistorySelected ? getApiUrl(apiList[key], { userJobScheduleId: websiteVulnerabilityId }) : getApiUrl(apiList[key], { userJobScheduleId });

  try {
    dispatch(setIsLoadings({ key: key, value: true }));
    const response = await axiosIn.get(apiUrl);
    dispatch(setIsLoadings({ key: key, value: false }));
    const data = response?.data ?? null;
    dispatch(setPersist({ key, value: data }));
    return data;
  } catch (error: any) {
    console.error(`Report request failed: ${key}`, error);
    dispatch(setIsLoadings({ key: key, value: false }));
    dispatch(setPersist({ key, value: null }));
  }
};

export const independent_initializers = async (key = null) => {
  const state: any = store.getState();
  const authToken = state.authReducer?.authToken;
  if (!authToken) return;

  dispatch(setPersist({ key: 'is_initial_fetches_loading', value: true }));

  try {
    // Array of key names and corresponding API requests for independent initializers
    const initialApis = [
      // { key: 'users_me_profile', request: axiosIn.get(getApiUrl(apiConfig.users_me_profile)) },
      { key: 'schedule_scan', request: axiosIn.get(getApiUrl(apiConfig.schedule_scan)) },
      // { key: 'domains', request: axiosIn.get(getApiUrl(apiConfig.domains)) },
      { key: 'notifications', request: axiosIn.get(getApiUrl(apiConfig.notifications)) },
    ];

    // Process initial API requests
    const initialResponses = await Promise.allSettled(initialApis.map((api) => api.request));

    initialResponses.forEach((res: any, index) => {
      const { key } = initialApis[index];
      if (res.status === 'fulfilled') {
        const data = res?.value?.data ?? null;
        dispatch(setPersist({ key, value: data }));
      } else {
        console.error(`Initial request failed: ${key}`, res.reason);
        dispatch(setPersist({ key, value: null }));
      }
    });

    // Get the userJobScheduleId from the schedule_scan response
    const scheduleScanResponse: any = initialResponses.find((res, index) => initialApis[index].key === 'schedule_scan' && res.status === 'fulfilled');
    scheduleScanResponse ? setUserJobScheduleIdFromResData(scheduleScanResponse?.value?.data) : null;
  } catch (error: any) {
    console.error('Unexpected error during initial fetches:', error);
    dispatch(setToastMessage({ msg: error.message || 'Unexpected error', color: 'error' }));
  } finally {
    dispatch(setPersist({ key: 'is_initial_fetches_loading', value: false }));
  }
};
